if (!customElements.get('main-product')) {
  class MainProduct extends HTMLElement {
    abortController = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [() => this.classList.add('is-updating')];
    postProcessHtmlCallbacks = [() => setTimeout(() => this.classList.remove('is-updating'), 300)];
    cartDrawer = undefined;
    isQuickview = false;
    isUpdating = false;

    constructor() {
      super();
      this.isBundle = this.hasAttribute('data-bundle');
    }

    get form() {
      return this.querySelector(`form#${this.getAttribute('data-form-id')}`);
    }

    get submitButton() {
      return this.form.elements['add'];
    }

    get optionValues() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked, select'), (el) => ({
        name: el.dataset.name,
        value: el.value,
        id: el.dataset.optionValueId || el.selectedOptions?.[0].dataset.optionValueId,
      }));
    }

    get selectedVariant() {
      const selectedVariant = this.querySelector('[data-selected-variant]')?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    connectedCallback() {
      this.cartDrawer = document.querySelector('cart-drawer');
      this.addEventListener('submit', this.onSubmit.bind(this));
      this.addEventListener('change', this.onVariantChange.bind(this));
      this.isQuickview = this.closest('quickview-modal');
    }

    async getBundleItems(forceStandardBundle = false) {
      const bundleItems = this.querySelector('bundle-items');
      const hasComparatPrice = bundleItems.dataset.hasComparatPrice === 'true';
      const parentId = this.form.elements['id'].value;
      const bundleId = new Date().getTime();

      // Check if this is a subscription bundle
      const subscriptionWidget = this.querySelector('subscription-widget');
      const isSubscription =
        !forceStandardBundle &&
        typeof recharge !== 'undefined' &&
        recharge &&
        subscriptionWidget &&
        subscriptionWidget.querySelector('#subscription-purchase:checked');

      if (isSubscription) {
        try {
          // Get the bundle product data from the subscription widget
          const bundleProductHandle = subscriptionWidget.dataset.productBundleHandle;
          const bundleProductId = subscriptionWidget.dataset.productBundleId;
          const bundleCollectionId = subscriptionWidget.dataset.productBundleCollectionId;

          const sanitizeText = (value) => value.toLowerCase().trim();

          const getPlanId = (product, planName) => {
            // Parcourir tous les groupes de plans de vente
            for (const group of product.selling_plan_groups) {
              const plan = group.selling_plans.find(
                (plan) => sanitizeText(plan.selling_plan_name) === sanitizeText(planName)
              );
              if (plan) {
                return plan.selling_plan_id;
              }
            }
            // Si aucun plan n'est trouvé, retourner null ou lever une erreur
            console.error(`Plan non trouvé: ${planName}`);
            return null;
          };

          // Get the selected selling plan
          const selectPlans = subscriptionWidget.querySelector('#subscription-frequencies');
          const selectedOption = selectPlans.options[selectPlans.selectedIndex];

          const bundleSellingPlanName = selectedOption.dataset.sellingPlanName;

          // Get bundle product and its selling plan details
          const bundleProduct = await recharge.cdn.getCDNProduct(bundleProductId);
          const bundleProductVariant = bundleProduct.variants[0].id;

          // Generate a random string for the bundle ID
          const randomString = Math.random().toString(36).substring(2, 15);
          const rcBundleId = `${randomString}:${bundleProductId}`;

          // Create the bundle object for Recharge
          const bundle = {
            externalVariantId: String(bundleProductVariant),
            externalProductId: String(bundleProduct.external_product_id),
            selections: await Promise.all(
              bundleItems.selectedItems.map(async (item) => {
                const itemProduct = await recharge.cdn.getCDNProduct(item.productId);
                return {
                  collectionId: String(bundleCollectionId),
                  externalProductId: String(itemProduct.external_product_id),
                  externalVariantId: String(item.id),
                  quantity: Number(item.qty),
                  sellingPlan: Number(getPlanId(itemProduct, bundleSellingPlanName)),
                };
              })
            ),
          };

          // Get cart items from Recharge
          const cartItems = await recharge.bundle.getDynamicBundleItems(
            bundle,
            bundleProductHandle
          );

          // Transform items to match your format with required Recharge properties
          return cartItems.map((item) => ({
            id: item.id,
            quantity: item.quantity,
            selling_plan: item.selling_plan,
            properties: {
              _bundleId: bundleId,
              _parentProduct: parentId,
              _rc_bundle: rcBundleId,
              _rc_bundle_variant: parentId,
              _rc_bundle_parent: bundleProductId,
              _rc_bundle_collection_id: bundleCollectionId,
            },
          }));
        } catch (error) {
          console.error('Error processing subscription bundle:', error);
          throw error;
        }
      } else {
        // Regular non-subscription bundle logic
        const items = [...bundleItems.selectedItems].map((item) => {
          return {
            id: item.id,
            quantity: Number(item.qty),
            properties: {
              _bundleId: bundleId,
              _parentProduct: parentId,
            },
          };
        });


        if (hasComparatPrice) {
          const itemsQty = items.reduce((acc, item) => acc + item.quantity, 0);

          // Paliers de réduction en dur
          const discountTiers = [
            {
              min_qty: 9,
              discount_type: "percent",
              discount_value: 18.75293
            },
            {
              min_qty: 7,
              discount_type: "percent",
              discount_value: 12.50
            },
            {
              min_qty: 4,
              discount_type: "percent",
              discount_value: 6.247
            },
            {
              min_qty: 1,
              discount_type: "percent",
              discount_value: 0
            }
          ];

          // Trouver le palier de réduction applicable (du plus élevé au plus bas)
          const applicableDiscount = discountTiers.find(discount => itemsQty >= discount.min_qty);

          if (applicableDiscount) {
            items.map((item) => {
              item.properties['_discountType'] = applicableDiscount.discount_type;
              item.properties['_discountValue'] = applicableDiscount.discount_value;
            });
          }
        } else {
          const itemsQty = items.reduce((acc, item) => acc + item.quantity, 0);
          bundleItems.discounts.forEach((discount) => {
            if (itemsQty >= discount.min_qty) {
              items.map((item) => {
                item.properties['_discountType'] = discount.discount_type;
                item.properties['_discountValue'] = discount.discount_value;
              });
            }
          });
        }
        return items;
      }
    }

    /**
     * Form submit callback.
     *
     * @param {Event} event
     */
    async onSubmit(event) {
      event.preventDefault();

      // Vérification pour la personnalisation
      const customizeToggle = this.form.querySelector('#customize-toggle');
      const customText = this.form.querySelector('#custom_text');

      if (customizeToggle && customizeToggle.checked && customText && !customText.value.trim()) {
        this.handleErrorMessage(window.variantStrings.customization_error);
        this.submitButton?.classList.remove('disabled'); // Réactiver le bouton si l'erreur se produit
        return; // Empêcher l'ajout au panier
      }

      this.submitButton?.classList.add('disabled');

      const formData = this.isBundle ? new FormData() : new FormData(this.form);
      if (this.cartDrawer) {
        formData.append(
          'sections',
          this.cartDrawer?.getSectionsToRender().map((section) => section.section)
        );
        formData.append('sections_url', window.location.pathname);
      }

      if (this.isBundle) {
        const inQuickView = this.closest('quickview-modal');
        let items;

        if (inQuickView) {
          items = await this.getBundleItems(true);
        } else if (typeof recharge !== 'undefined' && recharge) {
          recharge.init({
            storeIdentifier: 'hydratis.myshopify.com',
            storefrontAccessToken:
              'strfnt_154bad3e0df7d4ded54edd0aa279d86d1c4f59ea2f8f9fc1c67e04ab7c3b18de',
            appName: 'Hydratis - Axome',
            appVersion: '1.0.0',
            loginRetryFn: () => {
              return recharge.auth.loginShopifyAppProxy().then((session) => {
                return session;
              });
            },
          });
          items = await this.getBundleItems(false);
        } else {
          console.warn(
            'Not in Quickview, but recharge is undefined. Forcing standard bundle logic for onSubmit.'
          );
          items = await this.getBundleItems(true);
        }

        items.forEach((item, index) => {
          formData.append(`items[${index}][id]`, item.id);
          formData.append(`items[${index}][quantity]`, item.quantity);
          if (item.properties) {
            for (const [key, value] of Object.entries(item.properties)) {
              formData.append(`items[${index}][properties][${key}]`, value);
            }
          }
          if (item.selling_plan) {
            formData.append(`items[${index}][selling_plan]`, item.selling_plan);
          }
        });
      }

      Cart.add(formData)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }

          if (this.isQuickview) {
            this.dispatchEvent(
              new CustomEvent('quickview:close', {
                bubbles: true,
                cancelable: true,
              })
            );
          }

          setTimeout(() => {
            this.dispatchEvent(
              new CustomEvent('quickview:refresh', {
                bubbles: true,
                cancelable: true,
              })
            );
          }, 500);
          this.onVariantChange(event);

          this.cartDrawer.renderContents(response);

          // Chercher l'élément cart-drawer-items ou cart-items à l'intérieur du cartDrawer
          const cartItemsElement =
            this.cartDrawer.querySelector('cart-drawer-items') ||
            this.cartDrawer.querySelector('cart-items');
          if (cartItemsElement && typeof cartItemsElement.updateLazySrc === 'function') {
            cartItemsElement.updateLazySrc();
          }
        })
        .catch((error) => {
          console.error(error);
          this.handleErrorMessage('Failed to add items to cart. Please try again.');
        })
        .finally(() => {
          this.submitButton?.classList.remove('disabled');
        });
    }

    /**
     * Variant change callback.
     *
     * @param {Event} event
     * @returns
     */
    onVariantChange(event) {
      if (this.classList.contains('is-updating')) return event.preventDefault();

      if (event.target.type === 'number' || event.target.name === 'id') {
        return;
      }

      if (
        this.isBundle &&
        (event.target.id === 'subscription-frequencies' ||
          event.target.getAttribute('name') === 'purchase_type' ||
          event.target.classList.contains('product__form'))
      ) {
        if (!event.target.classList.contains('product__form')) {
          this.onVariantChangeSubscription(event);
        }
        return;
      }

      const productUrl =
        event.target.dataset.productUrl ||
        this.pendingRequestUrl ||
        event.target.selectedOptions?.[0].dataset.productUrl ||
        this.dataset.url;

      this.pendingRequestUrl = productUrl;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      let sellingPlanId = event.target.dataset.sellingPlanId;

      if (event.target.id === 'subscription-frequencies') {
        const selectedOption = event.target.options[event.target.selectedIndex];
        sellingPlanId = selectedOption.dataset.sellingPlanId;
      }

      // Si c'est un changement de produit (nouvelle URL), mettre à jour l'URL directement
      if (shouldSwapProduct && !this.isQuickview && productUrl) {
        this.updateVariantURL({
          newProductUrl: productUrl,
          sellingPlanId: sellingPlanId,
          replaceHistory: true
        });
      }

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(
          productUrl,
          this.optionValues.map((value) => value.id),
          shouldSwapProduct && !this.isQuickview,
          sellingPlanId
        ),
        targetId: event.target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(!this.isQuickview)
          : this.handleUpdateProductInfo(sellingPlanId),
      });
    }

    /**
     * Variant change callback for subscription.
     *
     * @param {Event} event
     * @returns
     */
    onVariantChangeSubscription(event) {
      const subscriptionWidget = this.querySelector('subscription-widget');

      const productUrl = subscriptionWidget.dataset.url;

      this.pendingRequestUrl = productUrl;
      const shouldSwapProduct = false;
      let sellingPlanId = event.target.dataset.sellingPlanId;

      if (event.target.id === 'subscription-frequencies') {
        const selectedOption = event.target.options[event.target.selectedIndex];
        sellingPlanId = selectedOption.dataset.sellingPlanId;
      }

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(
          productUrl,
          [],
          shouldSwapProduct && !this.isQuickview,
          sellingPlanId
        ),
        targetId: event.target.id,
        callback: this.handleUpdateSubscriptionProductInfo(sellingPlanId),
      });
    }

    /**
     * Handle error message.
     *
     * @param {String/Object} errorMessage Comes from Shopify.
     * @returns {Void}
     */
    handleErrorMessage(errorMessage = false) {
      const errorWrapper = this.querySelector('[data-error-wrapper]');
      if (!errorWrapper || !errorMessage) return;

      window.dispatchEvent(new CustomEvent('formError', { detail: errorMessage }));
      errorWrapper.classList.toggle('hidden', !errorMessage);

      if (typeof errorMessage == 'string') {
        errorWrapper.textContent = errorMessage || '';
      }
    }

    /**
     * Set unavailable.
     *
     * @returns {Void}
     */
    setUnavailable() {
      if (!this.submitButton) return;
      this.submitButton.textContent = window.variantStrings.unavailable;
      this.querySelector('[data-product-info="price"]').classList.add('visually-hidden');
    }

    /**
     * Update live regions.
     *
     * @param {Document} html
     */
    updateLiveRegions(html) {
      this.querySelectorAll('[data-product-info]').forEach((element) => {
        HTMLUpdateUtility.viewTransition(
          element,
          html.querySelector(`[data-product-info="${element.getAttribute('data-product-info')}"]`),
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks
        );
      });
    }

    /**
     * Update specific subscription elements.
     *
     * @param {Document} html
     */
    updateSpecificSubscriptionElements(html) {
      // Update one-time purchase elements
      const oneTimePurchase = this.querySelector('.ax-one-time-purchase');
      const newOneTimePurchase = html.querySelector('.ax-one-time-purchase');
      if (oneTimePurchase && newOneTimePurchase) {
        // Update radio input
        const oneTimeInput = oneTimePurchase.querySelector('input[name="purchase_type"]');
        const newOneTimeInput = newOneTimePurchase.querySelector('input[name="purchase_type"]');
        if (oneTimeInput && newOneTimeInput) {
          oneTimeInput.checked = newOneTimeInput.checked;
        }

        // Update radio and label
        const oneTimeRadio = oneTimePurchase.querySelector('.ax-radio');
        const newOneTimeRadio = newOneTimePurchase.querySelector('.ax-radio');
        if (oneTimeRadio && newOneTimeRadio) {
          oneTimeRadio.className = newOneTimeRadio.className;
        }

        // Update label text
        const oneTimeLabelText = oneTimePurchase.querySelector('.ax-purchase-type-label-text');
        const newOneTimeLabelText = newOneTimePurchase.querySelector(
          '.ax-purchase-type-label-text'
        );
        if (oneTimeLabelText && newOneTimeLabelText) {
          oneTimeLabelText.innerHTML = newOneTimeLabelText.innerHTML;
        }
      }

      // Update subscription purchase elements
      const subscriptionPurchase = this.querySelector('.ax-subscription-purchase');
      const newSubscriptionPurchase = html.querySelector('.ax-subscription-purchase');
      if (subscriptionPurchase && newSubscriptionPurchase) {
        // Update radio input
        const subscriptionInput = subscriptionPurchase.querySelector('input[name="purchase_type"]');
        const newSubscriptionInput = newSubscriptionPurchase.querySelector(
          'input[name="purchase_type"]'
        );
        if (subscriptionInput && newSubscriptionInput) {
          subscriptionInput.checked = newSubscriptionInput.checked;
        }

        // Update radio and label
        const subscriptionRadio = subscriptionPurchase.querySelector('.ax-radio');
        const newSubscriptionRadio = newSubscriptionPurchase.querySelector('.ax-radio');
        if (subscriptionRadio && newSubscriptionRadio) {
          subscriptionRadio.className = newSubscriptionRadio.className;
        }

        // Update label text
        const subscriptionLabelText = subscriptionPurchase.querySelector(
          '.ax-purchase-type-label-text'
        );
        const newSubscriptionLabelText = newSubscriptionPurchase.querySelector(
          '.ax-purchase-type-label-text'
        );
        if (subscriptionLabelText && newSubscriptionLabelText) {
          subscriptionLabelText.innerHTML = newSubscriptionLabelText.innerHTML;
        }

        // Update subscription options
        const subscriptionOptions = subscriptionPurchase.querySelector(
          '#subscription-purchase-options'
        );
        const newSubscriptionOptions = newSubscriptionPurchase.querySelector(
          '#subscription-purchase-options'
        );
        if (subscriptionOptions && newSubscriptionOptions) {
          subscriptionOptions.innerHTML = newSubscriptionOptions.innerHTML;
        }

        // Update price percentage
        const priceElement = subscriptionPurchase.querySelector('.ax-subscription-purchase--price');
        const newPriceElement = newSubscriptionPurchase.querySelector(
          '.ax-subscription-purchase--price'
        );
        if (priceElement && newPriceElement) {
          const newPercentage = newPriceElement.getAttribute('data-percentage');
          if (newPercentage) {
            priceElement.setAttribute('data-percentage', newPercentage);
          }
        }

        // Update button prices
        const priceContainers =
          this.closest('main-product').querySelectorAll('product-button-price');

        if (priceContainers.length) {
          let subscriptionPrice = '';
          let comparePrice = '';

          if (newSubscriptionInput.checked) {
            subscriptionPrice = subscriptionPurchase.querySelector(
              '.ax-subscription-purchase--price'
            ).textContent;
            comparePrice = subscriptionPurchase.querySelector(
              '.ax-subscription-purchase--compare-price'
            )?.textContent;
          } else {
            subscriptionPrice = oneTimePurchase.querySelector(
              '.ax-one-time-purchase--price'
            ).textContent;
            comparePrice = oneTimePurchase.querySelector(
              '.ax-one-time-purchase--compare-price'
            )?.textContent;
          }

          priceContainers.forEach((priceContainer) => {
            if (!comparePrice) {
              priceContainer.innerHTML = subscriptionPrice;
            } else {
              priceContainer.innerHTML = `
                <ins>${subscriptionPrice}</ins>
                <del>${comparePrice}</del>
              `;
            }
          });
        }

        // Update product-bar__price
        const productBarPrice = this.closest('main-product').querySelector('.product-bar__price');
        if (productBarPrice) {
          let subscriptionPrice = '';
          let comparePrice = '';

          if (newSubscriptionInput.checked) {
            subscriptionPrice = subscriptionPurchase.querySelector(
              '.ax-subscription-purchase--price'
            ).textContent;
            comparePrice = subscriptionPurchase.querySelector(
              '.ax-subscription-purchase--compare-price'
            )?.textContent;
          } else {
            subscriptionPrice = oneTimePurchase.querySelector(
              '.ax-one-time-purchase--price'
            ).textContent;
            comparePrice = oneTimePurchase.querySelector(
              '.ax-one-time-purchase--compare-price'
            )?.textContent;
          }

          if (!comparePrice) {
            // Prix sans discount
            productBarPrice.innerHTML = `
              <div class="price">
                <div class="price__container">
                  <div class="price__regular">
                    <span>${subscriptionPrice}</span>
                  </div>
                </div>
              </div>
            `;
          } else {
            // Prix avec discount
            productBarPrice.innerHTML = `
              <div class="price price--on-sale">
                <div class="price__container">
                  <div class="price__sale">
                    <del>${comparePrice}</del>
                    <ins>${subscriptionPrice}</ins>
                  </div>
                </div>
              </div>
            `;
          }
        }
      }
    }

    /**
     * Build request URL with params.
     *
     * @param {String} url
     * @param {Array} optionValues
     * @param {Boolean} shouldFetchFullPage
     * @returns {String}
     */
    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage, sellingPlanId) {
      const params = [];
      const sectionId = this?.dataset.sectionId;

      sectionId && !shouldFetchFullPage && params.push(`section_id=${sectionId}`);

      //clean optionValues if empty
      optionValues = optionValues.filter((value) => value !== undefined);
      if (optionValues.length > 0) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      if (sellingPlanId) {
        params.push(`selling_plan=${sellingPlanId}`);
      }

      return `${url}?${params.join('&')}`;
    }

    /**
     * Initialize product swap utility. Used with combined listings.
     */
    initializeProductSwapUtility() {
      this.postProcessHtmlCallbacks.push((newNode) => {
        window?.Shopify?.PaymentButton?.init();
      });
    }

    /**
     * Render product info.
     *
     * @param {String} requestUrl
     * @param {String} targetId
     * @param {Function} callback
     */
    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          callback(html);
        })
        .then(async () => {
          // set focus to last clicked option value
          document.querySelector(`#${targetId}`)?.focus();
          setTimeout(() => {
            this.updateLazySrc();
          }, 100);

          // Régénérer le widget Skeepers après mise à jour des informations produit
          if (typeof SkeepersProductWidget !== 'undefined' && SkeepersProductWidget.generate) {
            try {
              await SkeepersProductWidget.generate();
            } catch (error) {
              console.warn('Erreur lors de la régénération du widget Skeepers:', error);
            }
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
    }

    /**
     * Handle swap product.
     *
     * @param {Boolean} updateFullPage
     * @returns {Function}
     */
    handleSwapProduct(updateFullPage) {
      return (html) => {
        if (!updateFullPage) {
          HTMLUpdateUtility.viewTransition(
            this,
            html.querySelector('main-product'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
          return;
        }

        document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

        HTMLUpdateUtility.viewTransition(
          document.querySelector('main'),
          html.querySelector('main'),
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks
        );

        const newProductModal = html.querySelector('product-modal');
        newProductModal.moved = true;
        HTMLUpdateUtility.viewTransition(
          document.querySelector('product-modal'),
          newProductModal,
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks
        );
      };
    }

    /**
     * Handle update product info.
     *
     * @returns {Void}
     */
    handleUpdateProductInfo(sellingPlanId) {
      return (html) => {
        this.updateLiveRegions(html);

        if (!this.selectedVariant) {
          return this.setUnavailable();
        }

        if (this.isQuickview) {
          return;
        }

        this.updateMedia();
        this.updateURL(sellingPlanId);
      };
    }

    updateLazySrc() {
      const productMedia = document.querySelector('product-media');
      if (!productMedia) {
        console.warn('Element product-media non trouvé dans updateLazySrc');
        return;
      }
      const lazySrcImages = productMedia.querySelectorAll('img[lazy-src]');
      const lazySrcsetSources = productMedia.querySelectorAll('source[lazy-srcset]');

      lazySrcImages.forEach((img) => {
        const srcValue = img.getAttribute('lazy-src');
        if (srcValue) {
          img.src = srcValue;
        } else {
          console.warn('Attribut lazy-src présent mais vide ou null', img);
        }
      });

      lazySrcsetSources.forEach((source) => {
        const srcsetValue = source.getAttribute('lazy-srcset');
        if (srcsetValue) {
          source.srcset = srcsetValue;
        } else {
          console.warn('Attribut lazy-srcset présent mais vide ou null', source);
        }
      });
    }

    /**
     * Handle update subscription product info.
     *
     * @returns {Function}
     */
    handleUpdateSubscriptionProductInfo(sellingPlanId) {
      return (html) => {
        this.updateSpecificSubscriptionElements(html);
        this.updateURL(sellingPlanId);

        this.classList.remove('is-updating');
      };
    }

    /**
     * Update media.
     *
     * @returns {Void}
     */
    updateMedia() {
      if (!this.selectedVariant || !this.selectedVariant.featured_media) return;

      const productMedia = document.querySelector('product-media');
      productMedia.setActiveMedia(this.selectedVariant.featured_media.id);
    }

    /**
     * Update URL.
     *
     * @returns {Void}
     */
    updateURL(sellingPlanId) {
      if (!this.selectedVariant) return;
      const params = new URLSearchParams(window.location.search);
      params[params.has('variant') ? 'set' : 'append']('variant', this.selectedVariant.id);
      if (sellingPlanId) {
        params[params.has('selling_plan') ? 'set' : 'append']('selling_plan', sellingPlanId);
      } else {
        params[params.has('selling_plan') && params.delete('selling_plan')];
      }
      window.history.replaceState({}, '', `${this.dataset.url}?${params.toString()}`);
    }

        /**
     * Méthode personnalisée pour changer l'URL de la variante
     * Cette méthode peut être appelée pour mettre à jour l'URL avec plus de contrôle
     *
     * @param {Object} options - Options de configuration
     * @param {Number} options.variantId - ID de la variante (optionnel, utilise la variante sélectionnée si non fourni)
     * @param {Number} options.sellingPlanId - ID du plan de vente (optionnel)
     * @param {Boolean} options.replaceHistory - Si true, remplace l'historique, sinon ajoute une nouvelle entrée
     * @param {Object} options.customParams - Paramètres personnalisés à ajouter à l'URL
     * @param {String} options.newProductUrl - URL complète du nouveau produit (pour changement complet d'URL)
     * @returns {String} - La nouvelle URL
     */
    updateVariantURL(options = {}) {
      const {
        variantId = this.selectedVariant?.id,
        sellingPlanId = null,
        replaceHistory = true,
        customParams = {},
        newProductUrl = null
      } = options;

      let newUrl;

      // Si on a une nouvelle URL de produit, l'utiliser directement
      if (newProductUrl) {
        // Nettoyer l'URL de base (enlever les paramètres existants)
        const baseUrl = newProductUrl.split('?')[0];
        const params = new URLSearchParams();

        // Ajouter le plan de vente si fourni
        if (sellingPlanId) {
          params.set('selling_plan', sellingPlanId);
        }

        // Ajouter des paramètres personnalisés
        Object.entries(customParams).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            params.set(key, value);
          }
        });

        // Construire la nouvelle URL
        const paramString = params.toString();
        newUrl = paramString ? `${baseUrl}?${paramString}` : baseUrl;

        // Mettre à jour le dataset.url pour refléter le nouveau produit
        this.dataset.url = baseUrl;
      } else {
        // Comportement standard pour les changements de variante sur le même produit
        if (!variantId) {
          console.warn('Aucune variante sélectionnée ou fournie');
          return;
        }

        // Créer les paramètres URL
        const params = new URLSearchParams(window.location.search);

        // Ajouter ou mettre à jour l'ID de la variante
        params.set('variant', variantId);

        // Gérer le plan de vente
        if (sellingPlanId) {
          params.set('selling_plan', sellingPlanId);
        } else {
          params.delete('selling_plan');
        }

        // Ajouter des paramètres personnalisés
        Object.entries(customParams).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            params.set(key, value);
          } else {
            params.delete(key);
          }
        });

        // Construire la nouvelle URL
        newUrl = `${this.dataset.url}?${params.toString()}`;
      }

      // Mettre à jour l'historique du navigateur
      if (replaceHistory) {
        window.history.replaceState({}, '', newUrl);
      } else {
        window.history.pushState({}, '', newUrl);
      }

      // Déclencher un événement personnalisé pour notifier le changement d'URL
      document.dispatchEvent(new CustomEvent('variant:url-updated', {
        detail: {
          variantId,
          sellingPlanId,
          url: newUrl,
          newProductUrl: newProductUrl,
          params: newProductUrl ? Object.fromEntries(new URLSearchParams(newUrl.split('?')[1] || '').entries()) : Object.fromEntries(new URLSearchParams(window.location.search).entries())
        }
      }));

      return newUrl;
    }
  }
  customElements.define('main-product', MainProduct);
}
